/**
 * AccountMenu — who is signed in, at the foot of the rail rather than the top bar.
 * Collapsed it is just the avatar tile; expanded it names the user with the
 * workspace beneath. Clicking opens a popover beside the rail carrying sign-out —
 * and pins the rail open (`onMenuChange`) so the menu can't be orphaned by a
 * mouseleave while it is up.
 */

import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import Sym from './Sym'
import { useAuth } from '../contexts/AuthContext'
import { RAIL_CLOSED, RAIL_OPEN, WORKSPACE } from '../config/shell'

interface Props {
  open: boolean
  onMenuChange: (menuOpen: boolean) => void
}

function Panel({ name, sub, left, onSignOut }: { name: string; sub: string; left: number; onSignOut: () => void }) {
  return (
    <div
      role="menu"
      className="fixed bottom-3.5 z-[70] w-[228px] overflow-hidden rounded-xl border border-line bg-panel shadow-cx"
      style={{ left }}
    >
      <div className="border-b border-line2 px-3.5 py-2.5">
        <p className="truncate text-[12.5px] font-semibold text-ink">{name}</p>
        <p className="truncate text-[11.5px] text-mut">{sub}</p>
      </div>
      <button
        onClick={onSignOut}
        role="menuitem"
        className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-[12.5px] text-bad transition-colors hover:bg-panel2"
      >
        <Sym name="logout" size={17} weight={400} />
        Sign out
      </button>
    </div>
  )
}

function Row({ name, sub, open, menu, onClick }: { name: string; sub: string; open: boolean; menu: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      aria-haspopup="menu"
      aria-expanded={menu}
      aria-label={`Account: ${name}`}
      className={clsx(
        'cx-navi flex h-[46px] w-full items-center overflow-hidden rounded-[10px]',
        open ? 'gap-[9px] px-1.5 hover:bg-panel2' : 'justify-center gap-0'
      )}
    >
      <span className="flex h-[34px] w-[34px] min-w-[34px] items-center justify-center rounded-[9px] border border-line bg-panel2 font-mono text-[11px] tracking-[.04em] text-mut">
        {name.charAt(0).toUpperCase()}
      </span>
      <span
        className={clsx('min-w-0 flex-1 overflow-hidden text-left', open ? 'w-auto' : 'w-0')}
        style={{ opacity: open ? 1 : 0, transition: 'opacity .14s .05s' }}
      >
        <span className="block truncate text-[12.5px] font-semibold leading-tight text-ink">{name}</span>
        <span className="block truncate text-[11px] leading-tight text-mut2">{sub}</span>
      </span>
      {open && <Sym name="unfold_more" size={16} className="text-mut2" />}
    </button>
  )
}

export default function AccountMenu({ open, onMenuChange }: Props): React.JSX.Element {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [menu, setMenu] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const show = (next: boolean) => {
    setMenu(next)
    onMenuChange(next)
  }

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) show(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  })

  const name = user?.full_name || user?.username || 'Group viewer'
  const sub = user?.email || WORKSPACE.name

  return (
    <div ref={ref} className="w-full">
      <Row name={name} sub={sub} open={open} menu={menu} onClick={() => show(!menu)} />
      {menu && (
        <Panel
          name={name}
          sub={sub}
          left={(open ? RAIL_OPEN : RAIL_CLOSED) + 8}
          onSignOut={() => {
            show(false)
            logout()
            navigate('/login')
          }}
        />
      )}
    </div>
  )
}
